/**
 * FAZ 7 - Funnel Intelligence
 * Micro Message Component
 * 
 * Hipnotik mikro mesajlar için component.
 * Segmente göre farklı mesajlar gösterir.
 * 
 * Kurallar:
 * - Hero'da değil, ilk scroll kırılma noktasında
 * - Fade-in: 600-800ms
 * - Sadece 1 kez görünür
 * - Düşük kontrast, geniş satır aralığı
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Segment bazlı mesajlar
const MESSAGES = {
  // 🟥 Kaçış - Sessizlik, mesaj YOK
  escape: null,
  
  // 🟨 Kararsız / Keşif - Ayna metinleri
  exploring: [
    {
      text: "Bazı zihinler, buraya gelmeden önce karar vermez.",
      subtext: "Sadece bir süredir sessiz kalan bir şeyi fark eder."
    },
    {
      text: "Bazen aramak gerekmez.",
      subtext: "Zihin, zaten hatırlamaya başlamıştır."
    },
    {
      text: "Burada duranların çoğu,",
      subtext: "durduklarını çok sonra fark eder."
    },
  ],
  
  // 🟩 İlgili / Uyanık - Ayna + yumuşak davet
  engaged: [
    {
      text: "Buraya kadar kalındığında,",
      subtext: "zihin genelde çoktan bir ritme girmiştir."
    },
    {
      text: "Bazıları burada durur. Bazıları bir katmana daha iner.",
      subtext: "Acele eden neredeyse hiç olmaz."
    },
  ],
};

// Routing mesajları
const ROUTING_MESSAGES = {
  observer: "Bazı zihinler, sadece bir süre izler.",
  deeper: "Bazı şeyler, ancak durulduğunda yer değiştirir.",
  ritual: "Bazı yerler, ancak geri gelindiğinde açılır.",
};

export const MicroMessage = ({ 
  segment, 
  visible = false,
  variant = 'default', // 'default' | 'observer' | 'deeper' | 'ritual'
  onShow,
  className = '',
}) => {
  const [shown, setShown] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const hasShownRef = useRef(false);
  
  // Mesaj seçimi (sadece 1 kez)
  useEffect(() => {
    if (visible && !hasShownRef.current && segment) {
      hasShownRef.current = true;
      setShown(true);
      
      // Rastgele mesaj seç
      const messages = MESSAGES[segment];
      if (messages && messages.length > 0) {
        setMessageIndex(Math.floor(Math.random() * messages.length));
      }
      
      onShow?.(segment);
    }
  }, [visible, segment, onShow]);
  
  // Kaçış modunda veya segment yoksa hiçbir şey gösterme
  if (segment === 'escape' || !segment) {
    return null;
  }
  
  // Routing variant
  if (variant !== 'default' && ROUTING_MESSAGES[variant]) {
    return (
      <AnimatePresence>
        {shown && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className={`micro-message micro-message--${variant} ${className}`}
          >
            <p className="micro-message__text micro-message__text--routing">
              {ROUTING_MESSAGES[variant]}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
  
  // Default segment mesajları
  const messages = MESSAGES[segment];
  if (!messages || messages.length === 0) {
    return null;
  }
  
  const currentMessage = messages[messageIndex];
  
  return (
    <AnimatePresence>
      {shown && currentMessage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className={`micro-message micro-message--${segment} ${className}`}
        >
          <p className="micro-message__text">
            {currentMessage.text}
          </p>
          {currentMessage.subtext && (
            <p className="micro-message__subtext">
              {currentMessage.subtext}
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MicroMessage;
